// ══════════════════════════════════════════════════════════════
// Word Banks — CVC words and high-frequency words
// Organized by phonics stage for controlled vocabulary
// ══════════════════════════════════════════════════════════════

import { shuffle } from "../util/random.js";

const CVC_BANKS = [
  { stage: "R0_R1", requiredLetters: ["a","m","s","t","p","f"], words: ["sat","mat","map","sap","tap","pat","fat","sam","pam"] },
  { stage: "R2", requiredLetters: ["a","m","s","t","p","f","i","n"], words: ["sit","sip","pin","pan","tin","tan","nap","nip","man","fan","fin","fit","tip","pit"] },
  { stage: "R3", requiredLetters: ["a","m","s","t","p","f","i","n","d","o","c","k"], words: ["cat","can","cap","cot","cod","dot","dip","dim","kid","kit","cop","con","kin","dock","kick"] },
  { stage: "R4", requiredLetters: ["a","m","s","t","p","f","i","n","d","o","c","k","u","b","g","e"], words: ["bug","bun","bus","cup","cub","gum","dug","tug","bed","bet","beg","get","gut","den","dub","pub","peg","keg","gun","bud"] },
];

export function cvcWordsUpTo(stageId) {
  const result = [];
  for (const bank of CVC_BANKS) {
    result.push(...bank.words);
    if (bank.stage === stageId) break;
  }
  return result;
}

/** Ordered candidate distractor set for a CVC word, BEFORE random
 * sampling. minPairs (words differing by exactly one letter — the
 * pedagogically-meaningful vowel/consonant substitutions) come first,
 * then everything else. This is a deterministic function of (word,
 * pool): it's the corpus filter's output, independent of any shuffle.
 *
 * cvcDistractors() samples `count` items from this set (shuffled).
 * Tests asserting a *corpus property* ("the filter produces both
 * vowel and consonant substitution shapes") should assert over THIS
 * candidate set, not the random sample — the property is about what
 * the pool can yield, not which 3 a given shuffle happened to pick. */
export function cvcDistractorCandidates(word, pool) {
  if (word.length !== 3) {
    return pool.filter((w) => w !== word);
  }
  const minPairs = pool.filter((w) => {
    if (w === word || w.length !== 3) return false;
    let diffs = 0;
    for (let i = 0; i < 3; i++) { if (w[i] !== word[i]) diffs++; }
    return diffs === 1;
  });
  const others = pool.filter((w) => w !== word && !minPairs.includes(w));
  return [...minPairs, ...others];
}

export function cvcDistractors(word, pool, count = 3) {
  if (word.length !== 3) return pool.filter((w) => w !== word).slice(0, count);
  const minPairs = pool.filter((w) => {
    if (w === word || w.length !== 3) return false;
    let diffs = 0;
    for (let i = 0; i < 3; i++) { if (w[i] !== word[i]) diffs++; }
    return diffs === 1;
  });
  const others = pool.filter((w) => w !== word && !minPairs.includes(w));
  // NOTE (FOLLOWUPS — pedagogy): the random slice can return 3
  // distractors all in one misconception category (e.g. all consonant
  // substitutions). Whether to GUARANTEE per-item category diversity
  // is a content/pedagogy call — flagged in FOLLOWUPS.md, not changed
  // here. Production sampling behavior is unchanged by this commit.
  return [...shuffle(minPairs), ...shuffle(others)].slice(0, count);
}

export const HFW_LIST = [
  // MA-1191 (D-1102-3): entries carry no per-word "tricky part" breakdown. The
  // ONE breakdown is HEART_WORD_IRREGULAR (src/engine/heartWordCallouts.ts); a
  // second per-entry copy here fed only a picture no served surface drew,
  // and it had drifted from the table (have, one, the, she, are).
  { order: 1,  word: "a",     type: "heart" },
  { order: 2,  word: "I",     type: "heart" },
  { order: 3,  word: "am",    type: "decodable" },
  { order: 4,  word: "at",    type: "decodable" },
  { order: 5,  word: "in",    type: "decodable" },
  { order: 6,  word: "it",    type: "decodable" },
  { order: 7,  word: "an",    type: "decodable" },
  { order: 8,  word: "can",   type: "decodable" },
  { order: 9,  word: "is",    type: "heart" },
  { order: 10, word: "and",   type: "decodable" },
  { order: 11, word: "the",   type: "heart" },
  { order: 12, word: "to",    type: "heart" },
  { order: 13, word: "of",    type: "heart" },
  { order: 14, word: "was",   type: "heart" },
  { order: 15, word: "we",    type: "heart" },
  { order: 16, word: "he",    type: "heart" },
  { order: 17, word: "me",    type: "heart" },
  { order: 18, word: "be",    type: "heart" },
  { order: 19, word: "she",   type: "heart" },
  { order: 20, word: "you",   type: "heart" },
  // Expanded set — top sight words by frequency, with sentence frames in HFW_FRAMES
  { order: 21, word: "have",  type: "heart" },
  { order: 22, word: "said",  type: "heart" },
  { order: 23, word: "are",   type: "heart" },
  { order: 24, word: "for",   type: "heart" },
  { order: 25, word: "with",  type: "heart" },
  { order: 26, word: "what",  type: "heart" },
  { order: 27, word: "here",  type: "heart" },
  { order: 28, word: "they",  type: "heart" },
  { order: 29, word: "their", type: "heart" },
  { order: 30, word: "this",  type: "heart" },
  { order: 31, word: "that",  type: "heart" },
  { order: 32, word: "do",    type: "heart" },
  { order: 33, word: "go",    type: "decodable" },
  { order: 34, word: "no",    type: "decodable" },
  { order: 35, word: "so",    type: "decodable" },
  { order: 36, word: "see",   type: "decodable" },
  { order: 37, word: "my",    type: "heart" },
  { order: 38, word: "by",    type: "heart" },
  { order: 39, word: "had",   type: "decodable" },
  { order: 40, word: "his",   type: "heart" },
  { order: 41, word: "her",   type: "heart" },
  { order: 42, word: "him",   type: "decodable" },
  { order: 43, word: "them",  type: "decodable" },
  { order: 44, word: "into",  type: "decodable" },
  { order: 45, word: "one",   type: "heart" },
  { order: 46, word: "two",   type: "heart" },
  { order: 47, word: "from",  type: "decodable" },
  { order: 48, word: "if",    type: "decodable" },
  { order: 49, word: "but",   type: "decodable" },
  { order: 50, word: "not",   type: "decodable" },
  { order: 51, word: "out",   type: "heart" },
  { order: 52, word: "now",   type: "heart" },
  { order: 53, word: "up",    type: "decodable" },
  { order: 54, word: "down",  type: "heart" },
  { order: 55, word: "make",  type: "heart" },
  { order: 56, word: "like",  type: "heart" },
  { order: 57, word: "come",  type: "heart" },
  { order: 58, word: "some",  type: "heart" },
];

const CONTROLLED_SENTENCES = [
  // Sentences here MUST be uniquely answerable by semantic context alone — no picture dependency.
  // Each is validated: only the target makes sense among the options.
  // `scene` (optional) names an illustration that can be displayed alongside.

  // ── ANIMAL ACTIONS (verbs, with distinct distractors) ────────────
  { template: "The fish can ___.", target: "swim", stage: "R2", distractors: ["red", "big", "hot"], scene: "fish_swimming" },
  { template: "The dog can ___.", target: "run", stage: "R2", distractors: ["red", "wet", "fun"], scene: "dog_running" },
  { template: "The frog can ___.", target: "hop", stage: "R3", distractors: ["red", "big", "wet"], scene: "frog_hopping" },
  { template: "The bee can ___.", target: "buzz", stage: "R4", distractors: ["red", "wet", "big"], scene: "bee_flying" },
  { template: "The duck likes to ___.", target: "swim", stage: "R4", distractors: ["red", "big", "hot"], scene: "duck_swimming" },
  { template: "Birds can ___.", target: "fly", stage: "R3", distractors: ["red", "wet", "big"] },
  { template: "Cats like to ___.", target: "nap", stage: "R3", distractors: ["red", "big", "hot"] },
  { template: "The cow says ___.", target: "moo", stage: "R3", distractors: ["red", "fast", "big"] },
  { template: "The sheep says ___.", target: "baa", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "The owl says ___.", target: "hoot", stage: "R4", distractors: ["red", "fast", "wet"] },
  { template: "Snakes ___ on the ground.", target: "slither", stage: "R5", distractors: ["red", "fly", "big"] },
  { template: "The mouse likes to ___.", target: "squeak", stage: "R5", distractors: ["red", "big", "wet"] },
  { template: "Rabbits like to ___.", target: "hop", stage: "R3", distractors: ["red", "big", "wet"] },
  { template: "The lion can ___.", target: "roar", stage: "R4", distractors: ["red", "big", "wet"] },
  { template: "Bears like to ___ in winter.", target: "sleep", stage: "R4", distractors: ["red", "big", "fly"] },

  // ── DESCRIPTION (adjectives) ────────────────────────────────────
  { template: "The sun is ___.", target: "hot", stage: "R3", distractors: ["run", "swim", "nap"], scene: "sun_shining" },
  { template: "Ice is ___.", target: "cold", stage: "R3", distractors: ["run", "swim", "hop"] },
  { template: "The cake is ___.", target: "sweet", stage: "R4", distractors: ["run", "swim", "fly"], scene: "cake_on_plate" },
  { template: "Honey tastes ___.", target: "sweet", stage: "R4", distractors: ["run", "fly", "hop"] },
  { template: "Lemons taste ___.", target: "sour", stage: "R4", distractors: ["run", "fly", "hop"] },
  { template: "The grass is ___.", target: "green", stage: "R3", distractors: ["run", "swim", "fly"] },
  { template: "The sky is ___.", target: "blue", stage: "R3", distractors: ["run", "swim", "fly"] },
  { template: "Snow is ___.", target: "white", stage: "R5", distractors: ["run", "swim", "fly"] },
  { template: "The night is ___.", target: "dark", stage: "R4", distractors: ["run", "swim", "fly"] },
  { template: "An elephant is ___.", target: "huge", stage: "R5", distractors: ["run", "swim", "fly"] },
  { template: "A baby is ___.", target: "small", stage: "R5", distractors: ["run", "swim", "fly"] },
  { template: "A turtle is ___.", target: "slow", stage: "R3", distractors: ["red", "swim", "hop"] },
  { template: "A cheetah is ___.", target: "fast", stage: "R3", distractors: ["red", "swim", "hop"] },

  // ── LOCATION / USE (nouns) ──────────────────────────────────────
  { template: "The cat sat on the ___.", target: "mat", stage: "R3", distractors: ["run", "swim", "hop"], scene: "cat_on_mat" },
  { template: "I sleep in my ___.", target: "bed", stage: "R3", distractors: ["run", "swim", "hop"], scene: "child_in_bed" },
  { template: "I drink from a ___.", target: "cup", stage: "R4", distractors: ["run", "swim", "fly"], scene: "cup_steaming" },
  { template: "The pig is in the ___.", target: "mud", stage: "R4", distractors: ["run", "swim", "fly"], scene: "pig_in_mud" },
  { template: "The ship sails on the ___.", target: "sea", stage: "R3", distractors: ["run", "hop", "fly"], scene: "ship_sailing" },
  { template: "Fish swim in the ___.", target: "sea", stage: "R3", distractors: ["red", "big", "hot"] },
  { template: "Wind blows the ___.", target: "flag", stage: "R5", distractors: ["red", "big", "wet"], scene: "flag_waving" },
  { template: "I write with a ___.", target: "pen", stage: "R3", distractors: ["run", "swim", "fly"] },
  { template: "I read a ___.", target: "book", stage: "R4", distractors: ["run", "swim", "fly"], scene: "book_open" },
  { template: "I drive a ___.", target: "car", stage: "R3", distractors: ["run", "swim", "fly"] },
  { template: "I ride a ___.", target: "bike", stage: "R5", distractors: ["run", "swim", "fly"] },
  { template: "I eat with a ___.", target: "fork", stage: "R5", distractors: ["run", "swim", "fly"] },
  { template: "Birds live in a ___.", target: "nest", stage: "R5", distractors: ["red", "big", "wet"] },
  { template: "Bees live in a ___.", target: "hive", stage: "R6", distractors: ["red", "big", "wet"] },
  { template: "I open the door with a ___.", target: "key", stage: "R4", distractors: ["red", "big", "wet"] },

  // ── BODY PARTS ──────────────────────────────────────────────────
  { template: "I see with my ___.", target: "eyes", stage: "R4", distractors: ["run", "swim", "fly"] },
  { template: "I hear with my ___.", target: "ears", stage: "R4", distractors: ["run", "swim", "fly"] },
  { template: "I smell with my ___.", target: "nose", stage: "R4", distractors: ["run", "swim", "fly"] },
  { template: "I walk with my ___.", target: "feet", stage: "R5", distractors: ["red", "big", "hot"] },
  { template: "I clap with my ___.", target: "hands", stage: "R5", distractors: ["red", "big", "hot"] },
  { template: "Dogs have four ___.", target: "legs", stage: "R4", distractors: ["red", "big", "wet"] },
  { template: "I taste with my ___.", target: "tongue", stage: "R6", distractors: ["red", "big", "fast"] },

  // ── FOOD ────────────────────────────────────────────────────────
  { template: "I drink ___ when I am thirsty.", target: "water", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Bread is made from ___.", target: "wheat", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "Cows give us ___.", target: "milk", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Bees make ___.", target: "honey", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Apples grow on a ___.", target: "tree", stage: "R5", distractors: ["red", "big", "wet"], scene: "tree_with_fruit" },

  // ── DAYS / TIMES ────────────────────────────────────────────────
  { template: "There are seven ___ in a week.", target: "days", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "There are twelve ___ in a year.", target: "months", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "I sleep at ___.", target: "night", stage: "R5", distractors: ["red", "big", "wet"], scene: "moon_night" },
  { template: "We play during the ___.", target: "day", stage: "R3", distractors: ["red", "big", "wet"] },

  // ── COMPARISONS (require comparison context) ─────────────────────
  { template: "An elephant is bigger than a ___.", target: "mouse", stage: "R5", distractors: ["run", "swim", "hot"] },
  { template: "A car is faster than a ___.", target: "bike", stage: "R5", distractors: ["red", "big", "wet"] },
  { template: "A tree is taller than a ___.", target: "flower", stage: "R6", distractors: ["red", "swim", "fly"], scene: "flower_bloom" },
  { template: "A whale is bigger than a ___.", target: "fish", stage: "R3", distractors: ["red", "big", "wet"] },
  { template: "A plane is faster than a ___.", target: "car", stage: "R3", distractors: ["red", "big", "wet"] },
  { template: "A mountain is taller than a ___.", target: "hill", stage: "R5", distractors: ["red", "big", "wet"] },

  // ── OPPOSITES ────────────────────────────────────────────────────
  { template: "The opposite of hot is ___.", target: "cold", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "The opposite of big is ___.", target: "small", stage: "R5", distractors: ["red", "fast", "fly"] },
  { template: "The opposite of fast is ___.", target: "slow", stage: "R3", distractors: ["red", "big", "fly"] },
  { template: "The opposite of day is ___.", target: "night", stage: "R5", distractors: ["red", "big", "fly"] },
  { template: "The opposite of up is ___.", target: "down", stage: "R3", distractors: ["red", "big", "fly"] },
  { template: "The opposite of yes is ___.", target: "no", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "The opposite of happy is ___.", target: "sad", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "The opposite of empty is ___.", target: "full", stage: "R5", distractors: ["red", "big", "fast"] },

  // ══════════════════════════════════════════════════════════════
  // EXPANDED CONTENT — multi-clause sentences, conjunctions,
  // vowel teams, r-controlled words, plurals, contractions, inferences.
  // ══════════════════════════════════════════════════════════════

  // ── TWO-CLAUSE: AND ──────────────────────────────────────────────
  { template: "I have a cat and a ___.", target: "dog", stage: "R3", distractors: ["red", "big", "run"] },
  { template: "I see the sun and the ___.", target: "moon", stage: "R5", distractors: ["red", "big", "run"], scene: "moon_night" },
  { template: "Mom and Dad love ___.", target: "me", stage: "R3", distractors: ["red", "big", "run"] },
  { template: "I like apples and ___.", target: "grapes", stage: "R5", distractors: ["red", "big", "run"] },
  { template: "Bees and butterflies can ___.", target: "fly", stage: "R5", distractors: ["red", "big", "swim"] },
  { template: "The cat sat down and went to ___.", target: "sleep", stage: "R4", distractors: ["red", "big", "run"] },
  { template: "I read a book and then ate ___.", target: "lunch", stage: "R5", distractors: ["red", "big", "run"] },

  // ── TWO-CLAUSE: BUT ──────────────────────────────────────────────
  { template: "I like cats, but I love ___.", target: "dogs", stage: "R4", distractors: ["red", "big", "run"] },
  { template: "The day was hot, but the night was ___.", target: "cold", stage: "R3", distractors: ["red", "big", "run"] },
  { template: "I am small, but my brother is ___.", target: "tall", stage: "R3", distractors: ["red", "wet", "fast"] },
  { template: "Fish swim, but birds ___.", target: "fly", stage: "R3", distractors: ["red", "big", "run"] },
  { template: "The cake is sweet, but lemons are ___.", target: "sour", stage: "R5", distractors: ["red", "big", "run"] },
  { template: "I tried hard, but I did not ___.", target: "win", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "Ice is cold, but fire is ___.", target: "hot", stage: "R3", distractors: ["red", "big", "fast"], scene: "sun_shining" },

  // ── TWO-CLAUSE: BECAUSE ──────────────────────────────────────────
  { template: "I am happy because it is my ___.", target: "birthday", stage: "R6", distractors: ["red", "big", "run"] },
  { template: "The plant grew because of the ___.", target: "sun", stage: "R3", distractors: ["red", "big", "run"], scene: "sun_shining" },
  { template: "I drank water because I was ___.", target: "thirsty", stage: "R5", distractors: ["red", "big", "run"] },
  { template: "I went to bed because I was ___.", target: "tired", stage: "R5", distractors: ["red", "big", "fast"], scene: "child_in_bed" },
  { template: "The grass is green because of ___.", target: "rain", stage: "R5", distractors: ["red", "big", "run"] },

  // ── TWO-CLAUSE: SO ───────────────────────────────────────────────
  { template: "It was raining, so I needed an ___.", target: "umbrella", stage: "R6", distractors: ["red", "big", "run"] },
  { template: "I was hungry, so I ate some ___.", target: "food", stage: "R3", distractors: ["red", "big", "run"] },
  { template: "The sun went down, so it got ___.", target: "dark", stage: "R4", distractors: ["red", "big", "fast"], scene: "moon_night" },
  { template: "I was cold, so I put on a ___.", target: "coat", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "The cup fell, so the milk ___.", target: "spilled", stage: "R6", distractors: ["red", "big", "run"] },
  { template: "I won the race, so I felt ___.", target: "proud", stage: "R6", distractors: ["red", "big", "fast"] },

  // ── INFERENCE: pick the word that completes the meaning ──────────
  { template: "I use a key to open the ___.", target: "door", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "I wear shoes on my ___.", target: "feet", stage: "R5", distractors: ["red", "big", "run"] },
  { template: "Birds build a ___ in a tree.", target: "nest", stage: "R5", distractors: ["red", "big", "fly"] },
  { template: "A baby cow is called a ___.", target: "calf", stage: "R5", distractors: ["red", "big", "run"] },
  { template: "We use soap to get ___.", target: "clean", stage: "R5", distractors: ["red", "big", "run"] },
  { template: "The thunder is loud, but the rain is ___.", target: "soft", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "When you mix red and yellow you get ___.", target: "orange", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "When you mix blue and yellow you get ___.", target: "green", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "I plant a seed and water it to grow a ___.", target: "flower", stage: "R5", distractors: ["red", "big", "run"], scene: "flower_bloom" },

  // ── VOWEL TEAMS (ai, ee, oa, ay) ─────────────────────────────────
  { template: "The cloud will bring ___.", target: "rain", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "I sail on a ___.", target: "boat", stage: "R5", distractors: ["red", "big", "fast"], scene: "ship_sailing" },
  { template: "Honey bees live in a ___.", target: "tree", stage: "R5", distractors: ["red", "big", "fast"], scene: "tree_with_fruit" },
  { template: "I see a ___ in the sky.", target: "cloud", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "The duck likes the ___.", target: "pond", stage: "R5", distractors: ["red", "big", "fast"], scene: "duck_swimming" },
  { template: "We play on the ___.", target: "beach", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "I wear my ___ to school.", target: "coat", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "I love a sunny ___.", target: "day", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "Cookies are made with ___.", target: "dough", stage: "R6", distractors: ["red", "big", "run"] },
  { template: "Butterflies sip from a ___.", target: "flower", stage: "R6", distractors: ["red", "big", "run"], scene: "flower_bloom" },

  // ── R-CONTROLLED VOWELS (ar, er, ir, or, ur) ─────────────────────
  { template: "I drive a ___.", target: "car", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Stars come out at night and the moon is in the ___.", target: "sky", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "A baby horse is called a ___.", target: "colt", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "I wear a hat on my ___.", target: "head", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "I write with a ___.", target: "marker", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "The ___ keeps me warm.", target: "fire", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Birds are covered in ___.", target: "feathers", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "The ___ shines in the day.", target: "sun", stage: "R3", distractors: ["red", "big", "fast"], scene: "sun_shining" },

  // ── PLURALS ──────────────────────────────────────────────────────
  { template: "One cat. Two ___.", target: "cats", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "One dog. Three ___.", target: "dogs", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "One bus. Two ___.", target: "buses", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "One fish. Many ___.", target: "fish", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "One child. Two ___.", target: "children", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "One foot. Two ___.", target: "feet", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "One mouse. Two ___.", target: "mice", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "One leaf. Many ___.", target: "leaves", stage: "R6", distractors: ["red", "big", "fast"] },

  // ── CONTRACTIONS / SHORT FORMS ───────────────────────────────────
  { template: "I am tall. I'm not ___.", target: "small", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "She does not eat meat. She ___ eat meat.", target: "doesn't", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "It is a sunny day. ___ a sunny day.", target: "It's", stage: "R5", distractors: ["red", "big", "fast"] },

  // ── PAST TENSE / VERBS ───────────────────────────────────────────
  { template: "Today I run. Yesterday I ___.", target: "ran", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "Today I see. Yesterday I ___.", target: "saw", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Today I eat. Yesterday I ___.", target: "ate", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "Today I go. Yesterday I ___.", target: "went", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "Today I read. Yesterday I read a ___.", target: "book", stage: "R5", distractors: ["red", "big", "fast"], scene: "book_open" },

  // ── FEELINGS / EMOTIONS ──────────────────────────────────────────
  { template: "When I get a present, I feel ___.", target: "happy", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "When I lose my toy, I feel ___.", target: "sad", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "When something pops loudly, I feel ___.", target: "scared", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "When I learn something new, I feel ___.", target: "smart", stage: "R5", distractors: ["red", "big", "fast"] },

  // ── WEATHER / SEASONS ────────────────────────────────────────────
  { template: "Snow falls in ___.", target: "winter", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Flowers bloom in ___.", target: "spring", stage: "R5", distractors: ["red", "big", "fast"], scene: "flower_bloom" },
  { template: "We swim a lot in ___.", target: "summer", stage: "R5", distractors: ["red", "big", "fast"], scene: "duck_swimming" },
  { template: "Leaves fall in ___.", target: "fall", stage: "R3", distractors: ["red", "big", "run"] },
  { template: "The weather today is sunny and ___.", target: "warm", stage: "R5", distractors: ["red", "big", "fast"] },

  // ── COUNTING / SIZE ──────────────────────────────────────────────
  { template: "An ant is very ___.", target: "tiny", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "A whale is very ___.", target: "huge", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "There are seven days in a ___.", target: "week", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "There are four seasons in a ___.", target: "year", stage: "R5", distractors: ["red", "big", "fast"] },

  // ── CAUSE & EFFECT (longer sentences) ────────────────────────────
  { template: "The wind blew hard, and the leaves began to ___.", target: "fall", stage: "R5", distractors: ["red", "big", "swim"] },
  { template: "I forgot my umbrella, so my coat got ___.", target: "wet", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "The dog wagged its tail because it was ___.", target: "happy", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "The puppy was tired, so it took a ___.", target: "nap", stage: "R3", distractors: ["red", "big", "fast"] },
  { template: "The kite went up high because the wind was ___.", target: "strong", stage: "R6", distractors: ["red", "big", "fast"] },

  // ── COMPARATIVES (extra) ─────────────────────────────────────────
  { template: "A bee is small, but a ladybug is even ___.", target: "smaller", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "An elephant is heavier than a ___.", target: "mouse", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "A jet is faster than a ___.", target: "train", stage: "R5", distractors: ["red", "big", "fast"] },
  { template: "Of all my friends, Sam is the ___.", target: "kindest", stage: "R6", distractors: ["red", "big", "fast"] },
  { template: "The lake is deep, but the ocean is ___.", target: "deeper", stage: "R6", distractors: ["red", "big", "fast"] },
];

export function sentencesForStage(stageId) {
  const stageOrder = ["R0_R1","R2","R3","R4","R5"];
  const maxIdx = stageOrder.indexOf(stageId);
  if (maxIdx < 0) return CONTROLLED_SENTENCES;
  return CONTROLLED_SENTENCES.filter((s) => stageOrder.indexOf(s.stage) <= maxIdx);
}
